import { ReactElement, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikProps, getIn } from 'formik';
import FormContainer from '@pages/FormContainer';
import { defaultForm, InfringementExtensionMetaData, InfringementExtensionSchema, iInfringementExtensionFormValues, getSubmissionPayload } from '@services/InfringementExtensionService';
import { FormNavFooter, FormLoading, Alert, ProgressBar } from '@components/index';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga';
import { FormType } from '@constants';
import { iToolTips, iCountry, iApiError, iFormNavActions, iFVSCheckboxAll, iPartialFormData } from '@interfaces/interfaces';
import { getCountryList, getPartialForm, getTooltips, savePartialForm, submitSavedPartialForm } from '@api';

import { navigateToError, getStringIndex, navigateToNextStep, getDateForValidateDebtor, setFormDataValues, setCurrentFormikValues, navToError } from '@utils';

import ErrorContent from '@components/Content/AlertContent/ErrorContent';

import VicFinesContext from '../VicFinesFormContext';
import { InfringementExtErrorPage, InfringementExtStep0, InfringementExtStep1, InfringementExtStep10, InfringementExtStep2, InfringementExtStep3, InfringementExtStep4, InfringementExtStep5, InfringementExtStep6, InfringementExtStep7, InfringementExtStep8, InfringementExtStep9 } from '.';

import IssueIcon from '../../../images/issue.svg';

const {
  REACT_APP_SITECORE_API_ENDPOINT: API_URL,
  REACT_APP_PATH_INFRINGEMENT_EXTENSION: PATH_IE,
} = process.env;

function InfringementExtension(): ReactElement {
  const formikValuesRef = useRef({} as FormikProps<iInfringementExtensionFormValues>);
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
    const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;
  const pathNameUpperCase = location.pathname.toLowerCase();
  const currentStep = pathNameUpperCase === `${PATH_IE}` || pathNameUpperCase === `${PATH_IE}/` ? 'step0' : `${getStepInPath}`;

  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const [activeError, setActiveError] = useState(false);
  const partialFormAttachmentFields = ['SelectedObligationsStep9', 'SupportingAttachments', 'SignedDeclarationAttachment', 'OtherSupportingAttachments'];
  const arrayFieldsToBeCleared = [
    'ActionsTakenOnFine',
    'obligations',
    'infringementFineArray',
    'enteredObligationsArray',
    'InfringementNums',
    'AgencyNames',
    'AOffence',
    'DAOffence',
  ];

  // const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.InfringementExtensionForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
    const handlePopstate = () => {
      const isSubmitted = formikValuesRef.current && formikValuesRef.current.values ? formikValuesRef.current.values?.submitted : false;

      if (window.location.pathname === '/infringement-extension/step9' || isSubmitted) {
        navigate({
          pathname: '/infringement-extension/step10',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/infringement-extension/step2') && !isSubmitted) {
        navigate({
          pathname: '/infringement-extension/step3',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/infringement-extension/error') && !isSubmitted) {
        navigate({
          pathname: '/infringement-extension/error',
          search: location.search,
        });
      }
      if ((window.location.pathname === '/infringement-extension/step1') && !isSubmitted && formikValuesRef && formikValuesRef.current) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
          formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
          formikValuesRef.current.setFieldValue('OFFENCE_DATE', '');
          formikValuesRef.current.setFieldValue('DRIVER_LICENCE', '');
        }
      };
    window.addEventListener('popstate', handlePopstate);
    // document.addEventListener('keyup', redirectUrl);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    //   document.removeEventListener('keydown', redirectUrl);
    };
  }, []);

  const postObligation = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/InfringementExtension/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        const nextPageUrl = `${pathNameLessLastSegment}${'/step2'}`;
        if (responseBody.obligationNumber) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', responseBody.obligationNumber);
        }
        if (responseBody.Message === 'showBoth') {
          formikValuesRef.current.setFieldValue('isOfficerIssued', true);
        } else if (responseBody.Message === 'showFourteenDays') {
          formikValuesRef.current.setFieldValue('isOfficerIssued', false);
        }
        setApiError({
          Error: '',
        });
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };
  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: FormType.INFRINGEMENTEXTENSION,
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (response.status === 200) {
        if (responseBody.PartialFormID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID, formikContext);
        if (responseBody.CheckingExisting === 'no' && responseBody.CanSave) {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step3`);
          window.scrollTo(0, 0);
          return;
        }
        if (responseBody.PartialFormID) {
          getPartialForm(responseBody.PartialFormID)
            .then((resBody) => {
              if (typeof resBody === 'string') {
                if (resBody === 'Unauthorised') {
                  navigateToError();
                }
              } else {
                const formData = JSON.parse(resBody.FormData);
                const keys = Object.keys(resBody);
                keys.forEach((key) => {
                  if (key === 'FormData') {
                    setFormDataValues(formData, formikValuesRef.current.values.FormData, formikValuesRef, 'FormData');
                    setCurrentFormikValues(formData, formikValuesRef.current.values, formikValuesRef);
                  } else if (resBody[key]) {
                    formikContext.setFieldValue(`${key}`, resBody[key]);
                  }
                });
              }
              setLoading(false);
            })
            .catch((e) => console.log(e));
        } else {
          setLoading(false);
        }
        navigate(`${pathNameLessLastSegment}/step5`);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      navigateToError();
    }
  };

  const resetPFormDataValues = (partialFormApi: any, formikObject: any) => {
    if (partialFormApi && formikObject) {
      // clone data format like objectkey: [{},{}]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'object'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          type dynamicObj = {
            [key: string]: string;
          };
          const temp = {} as dynamicObj;
          Object.keys(val).forEach((key) => {
            temp[key] = val[key];
          });
          formikObject.push(temp);
        });
      }
      // handle scenario if formikObject is empty array , it's for ObjectKey:[] or string[]
      if (
        Array.isArray(partialFormApi)
        && partialFormApi[0]
        && typeof partialFormApi[0] === 'string'
        && Array.isArray(formikObject)
        && (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          formikObject.push(val);
        });
      }
      if (partialFormApi && formikObject) {
        Object.keys(partialFormApi).forEach((key) => {
          if (key in formikObject) {
            if (typeof partialFormApi[key] === 'object' && !(partialFormApi[key] instanceof Date)) {
              if (partialFormAttachmentFields.includes(key) || arrayFieldsToBeCleared.includes(key)) {
                // eslint-disable-next-line no-param-reassign
                formikObject[key] = [];
              }
              resetPFormDataValues(partialFormApi[key], formikObject[key]);
            } else {
              // eslint-disable-next-line no-param-reassign
              formikObject[key] = partialFormApi[key];
            }
          }
        });
      }
    }
  };

  const step2NextPage = () => {
    navigateToNextStep(navigate, location, 'next');
  };
  const validateDebtor = async (formikContext: any): Promise<void> => {
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    // const dateObj = getTheDate.split('/');
    // const specificedDateString = `${dateObj[2]}-${dateObj[1]}-${dateObj[0]}`;
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/InfringementExtension/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        if (responseBody === '/step4') {
          step2NextPage();
        }
      }
    } catch (error) {
      navigateToError();
    }
    checkExistingForm(formikContext);
  };

  const createPartialForm = async (formikContext: any): Promise<void> => {
    if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
      navigateToNextStep(navigate, location, 'next');
      return;
    }
    setLoading(true);
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1', ...formikContext.values.FormData }),
      FormTypeID: 8,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) {
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
          formikValuesRef.current.setFieldValue('Obligations', responseBody.Obligations);
        }
        navigateToNextStep(navigate, location, 'next');
        return;
      }
      navigateToError();
    } catch (error) {
      console.log(error);
    }
  };
  const updatePartialFormData = async (formikContext: any): Promise<boolean> => {
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const partialformPayload: iPartialFormData = {
      ID: formikContext.values.PartialFormID,
      Obligations: formikContext.values.Obligations,
      Attachments: [],
      FormData: JSON.stringify(formikContext.values.FormData), // {a: '1'},formikContext.values.FormData
      DebtorID: formikContext.values.DebtorID,
      FormTypeID: 8,
      Email: formikContext.values.saveEmail,
      Mobile: formikContext.values.saveMobile,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      EMRemeinderSent: formikContext.values.EMRemeinderSent,
      SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
      CreateDate: formikContext.values.CreateDate,
      ExpiryDate: formikContext.values.ExpiryDate,
    };
    try {
      const response = await savePartialForm(partialformPayload);
      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
  const step3NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  const fetchRegObligationNums = (selectedObligations: iFVSCheckboxAll[], authorisedObligation: string) => [
    authorisedObligation,
    ...selectedObligations.filter((e: iFVSCheckboxAll) => e.checked).map(({ number }: { number: string }) => number),
  ];
  const step5NextPage = async (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    setLoading(true);
    const payload = fetchRegObligationNums(formikContext.values.SelectedObligationsStep5, formikContext.values.AuthorisedObligation.Number);
    await formikContext.setFieldValue('SelectedObligations', payload);
    navigateToNextStep(navigate, location, 'next');
    setLoading(false);
  };
  const step6NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  const step7NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  const step8NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };
  const step9NextPage = (
    formikContext: any
    ) => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    navigateToNextStep(navigate, location, 'next');
  };

  const formNavActions: iFormNavActions = {
    step1: {
        next: postObligation,
      },
      step2: {
        next: validateDebtor,
      },
      step3: {
        next: step3NextPage,
      },
      step4: {
        next: createPartialForm,
      },
      step5: {
        next: step5NextPage,
      },
      step6: {
        next: step6NextPage,
      },
      step7: {
        next: step7NextPage,
      },
      step8: {
        next: step8NextPage,
      },
      step: {
        next: step9NextPage,
      }
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };

  const createPartialFormForSumbit = async (formValues: iInfringementExtensionFormValues): Promise<string> => {
    formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
    resetPFormDataValues(formikValuesRef.current.values, formValues.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formValues.DebtorID}`,
      EMRemeinderSent: false,
      Email: '',
      ExpiryDate: '',
      FormData: JSON.stringify({ ...formValues.FormData, SaveYesNo: 'true' }),
      FormTypeID: 8,
      ID: 0,
      IsSubscribedEM: false,
      IsSubscribedSMS: false,
      Mobile: '',
      Obligations: [
        {
          ID: 0,
          ObligationNO: formValues.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (responseBody.status === 200) {
          return 'true';
      }
      return responseBody;
    } catch (error) {
      return `${error}`;
    }
  };

  const submitForm = async (formValues: iInfringementExtensionFormValues): Promise<boolean> => {
    // const submissionNumber = '234';
    const formSubmissionValues = getSubmissionPayload(formValues);
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = `${API_URL}sitecore/InfringementExtension/SubmitInfringementExtensionForm`;
    let submitResponse = false;
      setLoading(true);
      submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          if (formValues.SaveYesNo === 'true') {
            // having partial form, close it
            submitSavedPartialForm(formValues.PartialFormID);
          }
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Infringement Extension Form',
            action: 'Submit',
            label: 'Infringement Extension Form',
          });
          // navigate to submission success
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          if (response.receiptNo) {
            formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
            return true;
          }
          navToError(navigate, location);
          return false;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        console.log(error);
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
      if (!submitResponse) return false;
      return true;
  };
  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={InfringementExtensionSchema}
      onSubmit={(values: iInfringementExtensionFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Infringement Extension">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                <ProgressBar formMetaData={InfringementExtensionMetaData} />
                {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
                <Routes>
                  <Route path="/step0/:ob" element={<InfringementExtStep0 formikRef={formikValuesRef} />} />
                  <Route path="/step1" element={<InfringementExtStep1 />} />
                  <Route path="/step2" element={<InfringementExtStep2 />} />
                  <Route path="/step3" element={<InfringementExtStep3 />} />
                  <Route path="/step4" element={<InfringementExtStep4 />} />
                  <Route path="/step5" element={<InfringementExtStep5 />} />
                  <Route path="/step6" element={<InfringementExtStep6 />} />
                  <Route path="/step7" element={<InfringementExtStep7 />} />
                  <Route path="/step8" element={<InfringementExtStep8 />} />
                  <Route path="/step9" element={<InfringementExtStep9 />} />
                  <Route path="/step10" element={<InfringementExtStep10 />} />
                  <Route path="/error" element={<InfringementExtErrorPage />} />
                  <Route path="/" index element={<InfringementExtStep0 formikRef={formikValuesRef} />} />
                </Routes>

                {!(location.pathname === `${PATH_IE}/step10` || location.pathname === `${PATH_IE}/error`) && (
                  <FormNavFooter
                    totalPages={10}
                    formNavActions={formNavActions}
                    submitPage={9}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={InfringementExtensionMetaData.pagesSegments[currentStep].validationKeys(values)}
                    formType={FormType.INFRINGEMENTEXTENSION}
                    firstPageNo={0}
                  />
                )}
              </Form>
            )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default InfringementExtension;
